import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

import { getCharsCountForService } from '@buffer-mono/char-toolkit'
import { toast } from '@buffer-mono/popcorn'

import type { FragmentOf } from '~publish/graphql'

import type { SavedReplyItem_SavedReply } from '../../SavedRepliesPopover/SavedReplyItem'
import { useCommunityConfig } from '../../../CommunityConfig'
import {
  extractSavedReplyText,
  insertText,
} from '../../../Comments/CommentReplyForm/utils/replyFormUtils'
import type { ReplyErrorType } from '../../../types'
import type { InsertedRange } from '../../EngagementInsertedTextHighlight'

import type {
  EngagementReplyFormChannel,
  EngagementReplyFormStatus,
} from './types'

type UseEngagementReplyDraftParams = {
  channel: EngagementReplyFormChannel
  textareaRef: RefObject<HTMLTextAreaElement>
  /**
   * Stable identity of the engagement. When it changes the card has been
   * recycled onto a different engagement, so the active highlight and the
   * locally-tracked replies (both belonging to the previous one) are dropped.
   */
  resetKey: string
  /** Lazy initial text, evaluated once at mount. */
  getInitialText: () => string
  /**
   * Text to seed into a still-pristine field once it becomes available. Channel
   * settings load async, so the mount-time seed is often empty; this re-seeds
   * the handle once they arrive without ever clobbering user input. Omit to
   * disable late seeding (Comments).
   */
  lateSeedText?: string
  /**
   * Optional draft persistence. When provided, `setText` saves and clear /
   * publish clear. Comments wire this to the reply-drafts store; Mentions omit
   * it.
   */
  persistence?: { save: (text: string) => void; clear: () => void }
  /** Fired on every status transition (Comments' `onReplyStatusChange`). */
  onStatusChange?: (status: EngagementReplyFormStatus) => void
  /** Fired once a reply publishes (Mentions' `onReplied`). */
  onPublished?: () => void
}

export type EngagementReplyDraft<TReply> = {
  // State
  text: string
  status: EngagementReplyFormStatus
  error?: string
  errorType?: ReplyErrorType
  submittingText?: string
  /** Locally-tracked optimistic replies, newest first (not in Apollo cache). */
  sentReplies: TReply[]
  insertedRange: InsertedRange | null

  // Refs
  savedRepliesRef: RefObject<HTMLDivElement>
  emojiPickerRef: RefObject<HTMLDivElement>

  // Text + insertion
  setText: (text: string) => void
  clearInsertedRange: () => void
  insertSuggestion: (text: string) => boolean
  insertSavedReply: (
    savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>,
  ) => void
  focus: () => void

  // Lifecycle
  clear: () => void
  discard: () => void

  // Status-machine transitions (driven by the compose hook + strategy)
  markProcessing: (submittingText: string) => void
  markPublished: (reply?: TReply) => void
  markError: (args: {
    message: string
    errorType?: ReplyErrorType
    restoreText: string
  }) => void
}

/**
 * The fully-shared reply *draft* core: text state, AI/saved-reply insertion,
 * the inserted-text highlight, the per-service character limit, focus, and the
 * `idle → processing → published | error` status-machine state — with no API,
 * mutation, or feature-specific reconciliation.
 *
 * Generic over `TReply` because the locally-tracked optimistic replies differ
 * per feature (Comments store `CommentData | LastReply`, Mentions store
 * `Mention | LastReply`); the draft only ever prepends what a strategy hands
 * `markPublished` and never inspects the shape.
 *
 * Consumed by `useEngagementReplyForm`, which composes it with an injected
 * submit strategy. Not meant for direct use by feature code.
 */
export const useEngagementReplyDraft = <TReply>({
  channel,
  textareaRef,
  resetKey,
  getInitialText,
  lateSeedText,
  persistence,
  onStatusChange,
  onPublished,
}: UseEngagementReplyDraftParams): EngagementReplyDraft<TReply> => {
  const { config } = useCommunityConfig()

  const [text, setTextState] = useState(getInitialText)
  const [status, setStatus] = useState<EngagementReplyFormStatus>('idle')
  const [error, setError] = useState<string>()
  const [errorType, setErrorType] = useState<ReplyErrorType>()
  const [submittingText, setSubmittingText] = useState<string>()
  const [sentReplies, setSentReplies] = useState<TReply[]>([])
  const [insertedRange, setInsertedRange] = useState<InsertedRange | null>(null)

  const savedRepliesRef = useRef<HTMLDivElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)

  // Guards the late-seed so it fires at most once and never clobbers input.
  const seedHandledRef = useRef(false)

  const setText = useCallback(
    (newText: string) => {
      setTextState(newText)
      persistence?.save(newText)
    },
    [persistence],
  )

  // Re-seed the handle once channel settings resolve, but only while the field
  // is still pristine. No-op when late seeding is disabled (Comments).
  useEffect(() => {
    if (lateSeedText === undefined) return
    if (seedHandledRef.current || !lateSeedText) return
    setTextState((prev) => (prev === '' ? lateSeedText : prev))
    seedHandledRef.current = true
  }, [lateSeedText])

  // Drop the active highlight and the locally-tracked replies when the card is
  // recycled onto a different engagement — both belong to the previous one.
  useEffect(() => {
    setInsertedRange(null)
    setSentReplies([])
  }, [resetKey])

  const clearInsertedRange = useCallback(() => {
    setInsertedRange(null)
  }, [])

  const focus = useCallback(() => {
    textareaRef.current?.focus()
  }, [textareaRef])

  const insertSuggestion = useCallback(
    (newText: string): boolean => {
      const finalText = insertText(text, newText)
      const maxChars = config?.getCharacterLimit(channel) ?? Infinity
      if (getCharsCountForService(finalText, channel.service) > maxChars) {
        toast.error(
          `Cannot insert text. The reply would exceed the ${maxChars} character limit.`,
          { duration: 3_000 },
        )
        return false
      }
      setText(finalText)
      // `insertText` always appends, so the inserted slice is the tail of
      // `finalText`. `EngagementTextarea` paints the rotating-gradient highlight
      // on this range until the animation completes or the user edits.
      setInsertedRange({
        start: finalText.length - newText.length,
        end: finalText.length,
      })
      textareaRef.current?.focus()
      return true
    },
    [text, textareaRef, setText, config, channel],
  )

  const insertSavedReply = useCallback(
    (savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>) => {
      if (!savedReply) return
      const contentText = extractSavedReplyText(savedReply)
      insertSuggestion(contentText)
    },
    [insertSuggestion],
  )

  const clear = useCallback(() => {
    setTextState('')
    persistence?.clear()
    setError(undefined)
    setErrorType(undefined)
    setSubmittingText(undefined)
    setStatus('idle')
    setInsertedRange(null)
    onStatusChange?.('idle')
  }, [persistence, onStatusChange])

  const discard = useCallback(() => {
    clear()
    onStatusChange?.('idle')
  }, [clear, onStatusChange])

  const markProcessing = useCallback(
    (nextSubmittingText: string) => {
      setError(undefined)
      setErrorType(undefined)
      setStatus('processing')
      setSubmittingText(nextSubmittingText)
      onStatusChange?.('processing')
    },
    [onStatusChange],
  )

  const markPublished = useCallback(
    (reply?: TReply) => {
      setStatus('published')
      setTextState('')
      persistence?.clear()
      setInsertedRange(null)
      if (reply) {
        setSentReplies((prev) => [reply, ...prev])
      }
      onStatusChange?.('published')
      onPublished?.()
    },
    [persistence, onStatusChange, onPublished],
  )

  const markError = useCallback(
    ({
      message,
      errorType: nextErrorType,
      restoreText,
    }: {
      message: string
      errorType?: ReplyErrorType
      restoreText: string
    }) => {
      setStatus('error')
      setText(restoreText) // Restore text for retry (persists for Comments)
      setError(message)
      setErrorType(nextErrorType)
      // Re-enable surrounding actions for retry (Comments' `onReplyStatusChange`
      // fired `idle` here; Mentions pass no callback).
      onStatusChange?.('idle')
    },
    [setText, onStatusChange],
  )

  return {
    text,
    status,
    error,
    errorType,
    submittingText,
    sentReplies,
    insertedRange,
    savedRepliesRef,
    emojiPickerRef,
    setText,
    clearInsertedRange,
    insertSuggestion,
    insertSavedReply,
    focus,
    clear,
    discard,
    markProcessing,
    markPublished,
    markError,
  }
}
